import { skipToken } from '@reduxjs/toolkit/query';
import Divider from '@rio-cloud/rio-uikit/Divider';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import Tag from '@rio-cloud/rio-uikit/Tag';
import {
    useCancelTransportOrderMutation,
    useGetTransportOrderQuery,
    useUpdateTransportOrderMutation,
} from 'api/transportOrdersApi';
import ConfirmDialog from 'components/ConfirmDialog';
import NoContentState from 'components/NoContentState';
import OrderStatusLabel from 'components/OrderStatusLabel';
import TransportOrderFormDialog from 'features/transport-order-form/TransportOrderFormDialog';
import { type TransportOrderFormType, toCreatePayload } from 'features/transport-order-form/transportOrderFormModel';
import { useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import { formatDateTime } from 'utils/formatDateTime';
import { formatRoute, getOrderStatus } from 'utils/transportOrder';

import styles from './TransportOrderDetail.module.scss';
import TransportOrderDetailFooter from './TransportOrderDetailFooter';
import TransportOrderDetailMap from './TransportOrderDetailMap';
import TransportOrderDetailMilestones from './TransportOrderDetailMilestones';
import TransportOrderDetailRow from './TransportOrderDetailRow';
import TransportOrderDetailSkeleton from './TransportOrderDetailSkeleton';
import TransportOrderDetailSummary from './TransportOrderDetailSummary';
import { transportOrderDetailClosed } from './transportOrderDetailSlice';

const PANEL_WIDTH = 480;
const HEADER_CLASS = 'text-size-h4 text-normal padding-x-10';

const detailSkeleton = <TransportOrderDetailSkeleton />;

export type TransportOrderDetailProps = {
    id: string | null;
};

const TransportOrderDetail = ({ id }: TransportOrderDetailProps) => {
    const dispatch = useAppDispatch();

    const { currentData, isFetching, isError, refetch } = useGetTransportOrderQuery(
        id === null ? skipToken : { transportOrderId: id }
    );

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

    const [updateTransportOrder] = useUpdateTransportOrderMutation();
    const [cancelTransportOrder, { isLoading: isCancelling }] = useCancelTransportOrderMutation();

    const handleEdit = () => setIsEditDialogOpen(true);
    const handleCancelOrder = () => setIsCancelDialogOpen(true);

    const confirmEdit = async (values: TransportOrderFormType) => {
        if (id === null) {
            return;
        }
        try {
            // PATCH accepts the same fields as create; the full assembled payload is a valid update.
            await updateTransportOrder({
                transportOrderId: id,
                updateTransportOrder: toCreatePayload(values),
            }).unwrap();
            setIsEditDialogOpen(false);
        } catch {
            // Keep the dialog open for retry. Error feedback is a follow-up.
        }
    };

    const confirmCancelOrder = async () => {
        if (id === null) {
            return;
        }
        try {
            await cancelTransportOrder({ transportOrderId: id }).unwrap();
            setIsCancelDialogOpen(false);
            dispatch(transportOrderDetailClosed());
        } catch {
            // Keep the confirmation open for retry.
        }
    };

    const renderContent = () => {
        if (isFetching && currentData === undefined) {
            return detailSkeleton;
        }
        if (isError) {
            return (
                <NoContentState
                    iconName='exclamation-sign'
                    headline='Detail could not be loaded'
                    action={{ text: 'Try again', onClick: refetch }}
                />
            );
        }
        if (currentData === undefined) {
            return null;
        }

        const status = currentData.deliveredAt === undefined ? getOrderStatus(currentData) : 'DELIVERED';

        return (
            <>
                <div className='margin-bottom-15'>
                    {status === undefined ? (
                        <Tag size='small' round={false} iconName='truck'>
                            {currentData.vin}
                        </Tag>
                    ) : (
                        <OrderStatusLabel status={status} condensed={false} />
                    )}
                </div>

                <TransportOrderDetailMap order={currentData} />

                <TransportOrderDetailSummary order={currentData} />

                <Divider spacing={10} className={styles.divider} />

                <TransportOrderDetailMilestones order={currentData} />

                <div className='margin-top-10 text-size-18 text-medium text-color-darkest'>Details</div>
                <Divider spacing={10} className={styles.divider} />
                <div className='display-grid grid-cols-2 gap-20'>
                    <TransportOrderDetailRow label='Load ID' value={currentData.loadId} />
                    <TransportOrderDetailRow label='Vehicle' value={currentData.vin} />
                    <TransportOrderDetailRow
                        label='Pickup window'
                        value={formatDateTime(currentData.pickupWindow.from)}
                        secondaryValue={formatDateTime(currentData.pickupWindow.to)}
                    />
                    <TransportOrderDetailRow
                        label='Delivery window'
                        value={formatDateTime(currentData.deliveryWindow.from)}
                        secondaryValue={formatDateTime(currentData.deliveryWindow.to)}
                    />
                </div>
            </>
        );
    };

    const route = currentData === undefined ? 'this order' : formatRoute(currentData);

    const routeMessage = (
        <span>
            Are you sure you want to cancel <b>{route}?</b> This cannot be undone.
        </span>
    );

    return (
        <>
            <Sidebar
                fly
                closed={!id}
                title={currentData === undefined ? 'Transport order' : formatRoute(currentData)}
                titleClassName={HEADER_CLASS}
                width={PANEL_WIDTH}
                position={Sidebar.RIGHT}
                footer={
                    /* TODO: it will be necessary to double check whether the transport order is still cancellable or editable
                     *   because its state may have changed in the meantime. */
                    currentData?.state === 'PLANNED' && (
                        <TransportOrderDetailFooter onEdit={handleEdit} onCancelOrder={handleCancelOrder} />
                    )
                }
                onClose={() => {
                    dispatch(transportOrderDetailClosed());
                }}
            >
                <div className='padding-x-20'>{renderContent()}</div>
            </Sidebar>
            <TransportOrderFormDialog
                show={isEditDialogOpen}
                transportOrder={currentData}
                onClose={() => setIsEditDialogOpen(false)}
                onSubmit={confirmEdit}
            />
            <ConfirmDialog
                show={isCancelDialogOpen}
                title='Cancel transport order'
                content={routeMessage}
                confirmButtonText='Cancel order'
                cancelButtonText='Keep order'
                disableConfirm={isCancelling}
                onConfirm={confirmCancelOrder}
                onCancel={() => setIsCancelDialogOpen(false)}
            />
        </>
    );
};

export default TransportOrderDetail;
